import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type ConsentSource = 'perfil' | 'cargar_cv' | 'postulacion' | 'ia' | 'sharing' | 'primer_login';

@Injectable({ providedIn: 'root' })
export class ConsentService {

  private hasActive$ = new BehaviorSubject<boolean | null>(null); // null = unknown
  // BehaviorSubject<source|null>: replay del último request al nuevo suscriptor.
  // Evita perder el evento si un componente lo dispara antes que AppComponent suscriba.
  private requestModal$ = new BehaviorSubject<ConsentSource | null>(null);

  constructor(private http: HttpClient) {}

  hasActiveSync(): boolean | null {
    return this.hasActive$.getValue();
  }

  changes(): Observable<boolean | null> {
    return this.hasActive$.asObservable();
  }

  /** AppComponent suscribe; emite la source cuando hay que abrir el modal. */
  modalRequests(): Observable<ConsentSource> {
    return this.requestModal$.asObservable().pipe(
      filter((s): s is ConsentSource => s !== null)
    );
  }

  /** Solicita la apertura del modal. */
  requestModal(source: ConsentSource): void {
    this.requestModal$.next(source);
  }

  /** Limpia el flag de modal (cuando se acepta o cierra). */
  clearModalRequest(): void {
    this.requestModal$.next(null);
  }

  syncFromServer(): Observable<boolean> {
    return this.http.get<any>(`${environment.baseApi}/privado/me/consent/status`).pipe(
      map(resp => !!resp?.data?.hasActiveConsent),
      tap(active => this.hasActive$.next(active)),
      catchError(err => {
        // 404 cuando el usuario logueado no es candidato/empresa (admin) —
        // expected, dejamos `false` y seguimos. Cualquier otro error sí lo
        // logueamos para no enmascarar fallas reales del backend.
        if (err?.status !== 404) {
          console.warn('[ConsentService] consent/status falló:', err?.status, err?.error?.message ?? err?.message);
        }
        this.hasActive$.next(false);
        return of(false);
      })
    );
  }

  accept(source: ConsentSource): Observable<any> {
    return this.http.post<any>(`${environment.baseApi}/privado/me/consent`, { source }).pipe(
      tap(() => this.hasActive$.next(true))
    );
  }

  revoke(): Observable<any> {
    return this.http.delete<any>(`${environment.baseApi}/privado/me/consent`).pipe(
      tap(() => this.hasActive$.next(false))
    );
  }

  reset(): void {
    this.hasActive$.next(null);
    this.requestModal$.next(null);
  }
}
